// src/components/analytics/components/tabs/ProfitAnalysis.js
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine
} from 'recharts';
import CustomTooltip from '../CustomTooltip';

const ProfitAnalysis = ({ data }) => {
    const theme = useTheme();

    return (
        <>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Profit Margin Analysis
            </Typography>
            <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={data}
                        margin={{ top: 10, right: 30, left: 20, bottom: 70 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12 }}
                            height={50}
                            angle={-45}
                            textAnchor="end"
                        />
                        <YAxis
                            yAxisId="left"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => `${value / 1000}k`}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => `${value}%`}
                            domain={[0, 100]}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend
                            verticalAlign="top"
                            height={36}
                            iconType="circle"
                            iconSize={10}
                        />

                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="transactions"
                            name="Income"
                            stroke={theme.palette.success.main}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="expenses"
                            name="Expenses"
                            stroke={theme.palette.error.main}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                        <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="profit"
                            name="Profit"
                            stroke={theme.palette.primary.main}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                        />
                        <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="profitMargin"
                            name="Profit Margin"
                            stroke={theme.palette.info.main}
                            strokeWidth={2}
                            strokeDasharray="3 3"
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                        />

                        <ReferenceLine
                            yAxisId="right"
                            y={50}
                            stroke={theme.palette.warning.main}
                            strokeDasharray="3 3"
                            label={{
                                value: 'Target Margin',
                                fill: theme.palette.warning.main,
                                position: 'insideBottomRight'
                            }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Box>

            <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                    Key Insights
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    The profit margin analysis shows the relationship between your income, expenses, and the resulting profit margin percentage over time. Months with higher margins indicate more efficient operations, while lower margins may require attention to expense management.
                </Typography>
            </Box>
        </>
    );
};

export default ProfitAnalysis;